
import { mapState } from "pinia";
import { useMap, useOrgSettings, useAccumulations } from "@/services";
import {
  formatSumReducer,
  formatValue,
  getCurrencySign,
  calculateTotalSumInsured,
} from "@/util/helpers";
import { DatasetRecord } from "@/types/DatasetRecord";
import { groupBy, startCase, toLower } from "lodash-es";
import { defineComponent, onMounted, ref, toRaw } from "vue";

export default defineComponent({
  name: "RecordsSummary",
  props: {
    icon: {
      type: String,
      default: "file-earmark-text-fill",
    },
    title: {
      type: String,
      default: "",
    },
    records: {
      type: Array as () => DatasetRecord[],
      default: () => [],
    },
    summaryProp: {
      type: String,
      required: true,
    },
    variant: {
      type: String,
      default: "info",
    },
    totalLabel: {
      type: String,
      default: "Total sum insured",
    },
    recordTypes: {
      type: Array as () => string[],
      default: () => [],
    },
  },
  setup() {
    const accumulationsStore = useAccumulations();
    const extraAccumulationDetails = ref();

    onMounted(async () => {
      const details = await accumulationsStore.loadBuildingCountData();

      if (details)
        extraAccumulationDetails.value = Object.fromEntries(
          Object.entries(details).filter(([key]) => key !== "NoBuildings")
        );
    });

    return {
      extraAccumulationDetails,
    };
  },
  computed: {
    ...mapState(useOrgSettings, ["currencySymbol"]),
    capacityWarningType(): any {
      return this.getMessage();
    },
    totalSumInsured(): number {
      return this.calculateTotalSumInsured(this.records);
    },
    capacityText() {
      return useOrgSettings().accumulations?.accumulationsLookups[0]
        ?.capacityWarnings;
    },
    multipleSumInsuredCapacity(): number {
      if (this.capacityText) {
        return (
          this.getSumInsuredValues(this.recordTypes[0]).get(
            this.capacityText?.sumInsuredColumn
          )?.value || 0
        );
      }
      return 0;
    },
    remainingCapacityVal(): number {
      if (this.isMultipleSumInsured(this.recordTypes[0])) {
        return this.maxCapacityVal - this.multipleSumInsuredCapacity;
      }
      return this.maxCapacityVal - this.calculateTotalSumInsured(this.records);
    },
    formatedRemaningCapacity(): string {
      return formatValue(toRaw(this.remainingCapacityVal), {
        digitsInfo: "1.2-2",
        currency: getCurrencySign(this.currencySymbol),
      });
    },
    maxCapacityVal(): number {
      if (this.capacityText) {
        return Math.max(
          ...this.capacityText?.thresholds.map((x) => x.greaterThan)
        );
      }
      return 0;
    },
    groupedRecords(): Record<string, DatasetRecord[]> {
      return groupBy(this.records, "lcId");
    },
  },
  methods: {
    getTypeRecords(type: string): DatasetRecord[] {
      return this.groupedRecords[type] || [];
    },
    isMultipleSumInsured(type: string): boolean {
      return this.getTypeRecords(type).some((x) => x.hasMultipleSumInsured);
    },
    // returns a map of sum insured display name and its sum value
    getSumInsuredValues(
      type: string
    ): Map<string, { displayName: string; value: number }> {
      return this.getTypeRecords(type).reduce((x, record) => {
        record.sumInsuredColumns.forEach((sumInsured) => {
          const sumInsuredValue = record.value[sumInsured.columnName];
          const sumInsuredKey = sumInsured.columnName;

          if (x.has(sumInsuredKey)) {
            x.set(sumInsuredKey, {
              displayName: sumInsured.displayName,
              value: x.get(sumInsuredKey)?.value + sumInsuredValue,
            });
          } else {
            x.set(sumInsuredKey, {
              displayName: sumInsured.displayName,
              value: sumInsuredValue,
            });
          }
        });
        return x;
      }, new Map<string, { displayName: string; value: number }>());
    },
    getMessage() {
      let message = {
        color: "",
        text: "",
      };

      if (this.capacityText?.thresholds?.length) {
        const warnings = this.capacityText.thresholds.sort(
          (a: any, b: any) => a.greaterThan - b.greaterThan
        );
        if (this.isMultipleSumInsured(this.recordTypes[0])) {
          warnings.forEach((x) => {
            if (this.multipleSumInsuredCapacity >= x.greaterThan) {
              message.color = x.colour;
              message.text = x.message;
            }
          });
        } else {
          warnings.forEach((x) => {
            if (this.totalSumInsured >= x.greaterThan) {
              message.color = x.colour;
              message.text = x.message;
            }
          });
        }
      }
      return message;
    },
    getLabelForKey(key: string): string {
      switch (key) {
        case "NoRDPs":
          return "RDPs";
        case "NoCDPs":
          return "CDPs";
        default:
          return startCase(toLower(key));
      }
    },
    formatSumReducer,
    formatValue,
    getCurrencySign,
    calculateTotalSumInsured,
    titleCase(name: string): string {
      return startCase(toLower(name));
    },
    isDatasetVisible(type: string) {
      return useMap().layerControls.find(({ id }) => id === type)?.isVisible;
    },
    toggleLayerVisibility(type: string) {
      const lc = useMap().layerControls.find(({ id }) => id === type);
      if (lc) {
        useMap().updateLayerGroup({
          path: lc?.parentGroup?.path || "",
          key: "isVisible",
          value: !lc.isVisible,
        });
      }
    },
  },
});
